/* eslint-disable react-hooks/exhaustive-deps */
import { useJsApiLoader, } from '@react-google-maps/api';
import L from 'leaflet';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Map, Marker, Popup, TileLayer } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import 'react-leaflet-markercluster/dist/styles.min.css';
import { useSelector } from 'react-redux';
import Spinner from '../../components/Spinner';
import { GOOGLE_MAP_API_KEY } from '../../configs';
import groupByCoors from '../../helpers/groupByCoors';
import InfoWindowCameraComponent from './InfoWindowCamera';
import InfoWindowGatewayComponent from './InfoWindowGateway';
import { WarningMarkers } from './WarningMarkers';

const TEXT_COLOR = 'white';
const ICON_CLUSTER_SIZE = 66;

const clusterCameraOptions = {
  styles: new Array(6).fill({
    textColor: TEXT_COLOR,
    url: process.env.PUBLIC_URL + '/icons/camera_cluster.svg',
    height: ICON_CLUSTER_SIZE,
    width: ICON_CLUSTER_SIZE,
  }),
};
const clusterGatewayOptions = {
  styles: new Array(6).fill({
    textColor: TEXT_COLOR,
    url: process.env.PUBLIC_URL + '/icons/gateway_cluster.svg',
    height: ICON_CLUSTER_SIZE,
    width: ICON_CLUSTER_SIZE,
  }),
};

const MapOfCameraComponent = (props) => {
  const { t } = useTranslation();
  const {
    cameras,
    gateways,
    mode,
    localMarker,
    internalGateways,
    getDeviceStatesMap,
    setKeyMap,
    setMode,
  } = props;

  const { detailDevicesMap } = useSelector((state) => state);

  const [listMarker, setListMarker] = useState();

  const searchBoxRef = useRef();
  const [centerCoors, setCenterCoors] = useState({
    lat: 21.028511,
    lng: 105.804817,
  });
  const [zoom, setZoom] = useState(8);
  const [currentLocation, setCurrentLocation] = useState();
  const [keyMapPopup, setKeyMapPopup] = useState(Math.random());
  const mapRef = useRef();

  useEffect(() => {
    if (mode === 0 && cameras) {
      setListMarker(
        groupByCoors(cameras).map((data) => ({ ...data, isOpen: false }))
      );
    }
  }, [cameras, mode]);

  useEffect(() => {
    if (mode === 1 && gateways) {
      setListMarker(
        groupByCoors(gateways).map((data) => ({ ...data, isOpen: false }))
      );
    }
  }, [mode]);

  useEffect(() => {
    if (localMarker) {
      setCenterCoors(localMarker);
      setZoom(15);
    }
  }, [localMarker]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        setCurrentLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      });
    }
  }, []);

  useEffect(() => {
    if (detailDevicesMap?.length > 0 && listMarker?.length > 0) {
      setListMarker((state) => [
        ...state.map((marker) => {
          const data = detailDevicesMap?.filter(
            (item) => marker.lat === item.lat && marker.lng === item.lng
          );
          if (data?.length > 0) {
            if (data?.find((item) => item.deviceType === 'gateway')) {
              return {
                ...marker,
                devices: data.map((gate) => ({
                  name: gate.name,
                  ownerId: gate.ownerId,
                  maxAvailable: gate.maxAvailable,
                  note: gate.note,
                  lat: gate.lat,
                  lng: gate.lng,
                  state: gate.state,
                  ipAddress: gate.ipAddress,
                  localAddress: gate.localAddress,
                  portScan: gate.portScan,
                  serial: gate.serial,
                  targetId: gate.targetId,
                  deviceType: gate.deviceType,
                  performance: {
                    cpu: gate.cpu,
                    temperature: gate.temperature,
                    ram: gate.ram,
                    disk: gate.disk,
                    totalDisk: gate.totalDisk,
                    networkIn: gate.networkIn,
                    networkOut: gate.networkOut,
                  },
                })),
              };
            }
            return {
              ...marker,
              devices: [...data],
            };
          }
          return { ...marker };
        }),
      ]);
    }
  }, [detailDevicesMap]);

  const onToggleOpen = (lat, lng) => {
    setListMarker((state) => [
      ...state.map((marker) => {
        if (marker.lat === lat && marker.lng === lng) {
          return { ...marker, isOpen: !marker.isOpen };
        }
        return { ...marker, isOpen: false };
      }),
    ]);
  };
  const onLoad = useCallback(
    (mapInstance) => {
      if (mapInstance && listMarker) {
        if (listMarker.length > 0) {
          var bounds = new window.google.maps.LatLngBounds();
          listMarker.map((marker) => {
            return bounds.extend({
              lat: marker.lat,
              lng: marker.lng,
            });
          });
          mapInstance.fitBounds(bounds);
        } else if (currentLocation) {
          var latlng = new window.google.maps.LatLng(currentLocation);
          mapInstance.setCenter(latlng);
          setZoom(15);
        }
      }
    },
    [currentLocation, listMarker]
  );
  const getDevices = useCallback(
    (devices) => {
      const deviceSerials = devices?.map((item) => item.serial);
      getDeviceStatesMap(deviceSerials);
    },
    [getDeviceStatesMap]
  );
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAP_API_KEY,
  });
  const renderMap = useCallback(() => {
    const CameraWarningMarker = new L.Icon({
      iconUrl: process.env.PUBLIC_URL + '/icons/camera_warning.svg',
      iconRetinaUrl: process.env.PUBLIC_URL + '/icons/camera_warning.svg',
      iconAnchor: null,
      shadowUrl: null,
      shadowSize: null,
      shadowAnchor: null,
      iconSize: new L.Point(32, 32),
    });

    const CameraMarker = new L.Icon({
      iconUrl: process.env.PUBLIC_URL + '/icons/camera_marker.svg',
      iconRetinaUrl: process.env.PUBLIC_URL + '/icons/camera_marker.svg',
      iconAnchor: null,
      shadowUrl: null,
      shadowSize: null,
      shadowAnchor: null,
      iconSize: new L.Point(32, 32),
    });
    const CameraMarkerOff = new L.Icon({
      iconUrl: process.env.PUBLIC_URL + '/icons/camera_marker_off.svg',
      iconRetinaUrl: process.env.PUBLIC_URL + '/icons/camera_marker_off.svg',
      iconAnchor: null,
      shadowUrl: null,
      shadowSize: null,
      shadowAnchor: null,
      iconSize: new L.Point(32, 32),
    });
    const GatewayMarker = new L.Icon({
      iconUrl: process.env.PUBLIC_URL + '/icons/gateway_marker.svg',
      iconRetinaUrl: process.env.PUBLIC_URL + '/icons/gateway_marker.svg',
      iconAnchor: null,
      shadowUrl: null,
      shadowSize: null,
      shadowAnchor: null,
      iconSize: new L.Point(36, 36),
    });
    const GatewayMarkerOff = new L.Icon({
      iconUrl: process.env.PUBLIC_URL + '/icons/gateway_marker_off.svg',
      iconRetinaUrl: process.env.PUBLIC_URL + '/icons/gateway_marker_off.svg',
      iconAnchor: null,
      shadowUrl: null,
      shadowSize: null,
      shadowAnchor: null,
      iconSize: new L.Point(36, 36),
    });

    const clusterCameraOptions = new L.Icon({
      iconUrl: process.env.PUBLIC_URL + '/icons/camera_cluster.svg',
      iconRetinaUrl: process.env.PUBLIC_URL + '/icons/camera_cluster.svg',
      iconAnchor: null,
      shadowUrl: null,
      shadowSize: null,
      shadowAnchor: null,
      iconSize: new L.Point(42, 52),
    });

    const clusterGatewayOptions = new L.Icon({
      iconUrl: process.env.PUBLIC_URL + '/icons/gateway_cluster.svg',
      iconRetinaUrl: process.env.PUBLIC_URL + '/icons/gateway_cluster.svg',
      iconAnchor: null,
      shadowUrl: null,
      shadowSize: null,
      shadowAnchor: null,
      iconSize: new L.Point(ICON_CLUSTER_SIZE, ICON_CLUSTER_SIZE),
    });

    const HoangSaMarker = new L.Icon({
      iconUrl: process.env.PUBLIC_URL + '/icons/hoang_sa.svg',
      iconRetinaUrl: process.env.PUBLIC_URL + '/icons/hoang_sa.svg',
      iconAnchor: null,
      shadowUrl: null,
      shadowSize: null,
      shadowAnchor: null,
      iconSize: new L.Point(50, 50),
    });

    const TruongSaMarker = new L.Icon({
      iconUrl: process.env.PUBLIC_URL + '/icons/truong_sa.svg',
      iconRetinaUrl: process.env.PUBLIC_URL + '/icons/truong_sa.svg',
      iconAnchor: null,
      shadowUrl: null,
      shadowSize: null,
      shadowAnchor: null,
      iconSize: new L.Point(60, 60),
    });

    function createKey({ lat, lng }) {
      return lat + lng;
    }

    return (
      <Map
        className="dashboard-map"
        zoom={zoom}
        center={centerCoors}
        ref={mapRef}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url={`https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=cb1_29qr_1_c0df2ae1e7a7e6440cbe3e3c`}
        />
        <MarkerClusterGroup
          animate={true}
          zoomToBoundsOnClick={true}
          iconCreateFunction={() =>
            mode === 0 ? clusterCameraOptions : clusterGatewayOptions
          }
        >
          {listMarker.map((mark, index) => (
            <Marker
              key={createKey(mark) + index}
              position={mark}
              icon={
                mode === 0
                  ? mark.devices?.find((item) => item.state === 1)
                    ? CameraMarker
                    : CameraMarkerOff
                  : mark.devices?.find((item) => item.state === 1)
                    ? GatewayMarker
                    : GatewayMarkerOff
              }
              onClick={() => {
                onToggleOpen(mark.lat, mark.lng);
                getDevices(mark.devices);
              }}
            >
              <Popup onClose={() => setKeyMapPopup(Math.random())}>
                {mode === 0 ? (
                  <InfoWindowCameraComponent
                    key={keyMapPopup}
                    devices={mark && mark.devices}
                    internalGateways={internalGateways}
                    setKeyMap={setKeyMap}
                  />
                ) : (
                  <InfoWindowGatewayComponent
                    key={keyMapPopup}
                    devices={mark && mark.devices}
                    setKeyMap={setKeyMap}
                    setMode={setMode}
                  />
                )}
              </Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>
        <Marker
          position={[16.103525, 111.919686]}
          icon={HoangSaMarker}
        ></Marker>
        <Marker
          position={[8.644641, 111.919686]}
          icon={TruongSaMarker}
        ></Marker>

        <WarningMarkers mode={mode} mapRef={mapRef} />
      </Map>
    );
  }, [
    centerCoors,
    internalGateways,
    isLoaded,
    listMarker,
    mode,
    onLoad,
    t,
    zoom,
    getDevices,
    keyMapPopup,
  ]);

  return listMarker && internalGateways ? renderMap() : <Spinner />;
};

export default MapOfCameraComponent;
